import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { Navbar } from '@/components/layout/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Order, AdminStats, Product, CountryConfig, IntegrationProviderConfig, IntegrationChannel, SubscriptionPlan, UserSubscription, ApiToken, TokenScope } from '@shared/types';
import { PRODUCTS } from '@shared/farm-data';
import { Loader2, RefreshCcw, AlertTriangle, TrendingUp, DollarSign, Package, Plus, Save, ToggleLeft, ToggleRight, Zap, CheckCircle, Clock, Check, X, Users, Store, Listings, Star, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useCurrency } from '@/hooks/use-currency';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

const CHART_COLORS = ['#2D6A4F', '#D08C60', '#111827', '#4b5563', '#9ca3af'];
export function AdminPage() {
  const queryClient = useQueryClient();
  const { formatFromBase } = useCurrency();
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api<AdminStats>('/api/admin/stats'),
  });
  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['admin-orders'],
    queryFn: () => api<{ items: Order[] }>('/api/admin/orders'),
  });

  const { data: integrationsData } = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: () => api<{ items: IntegrationProviderConfig[] }>('/api/admin/integrations'),
  });
  
  const { data: countriesData } = useQuery({
    queryKey: ['admin-countries'],
    queryFn: () => api<{ items: CountryConfig[] }>('/api/admin/countries'),
  });

  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery({
    queryKey: ['admin-subscriptions'],
    queryFn: () => api<{ items: UserSubscription[] }>('/api/admin/subscriptions'),
  });

  const { data: subscriptionPlansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api<SubscriptionPlan[]>('/api/subscription-plans'),
  });

  const { data: sellersData } = useQuery({
    queryKey: ['admin-sellers'],
    queryFn: () => api<{ items: any[] }>('/api/admin/sellers'),
  });

  const { data: listingsData } = useQuery({
    queryKey: ['admin-listings'],
    queryFn: () => api<{ items: any[] }>('/api/admin/listings'),
  });

  const { data: reviewsData } = useQuery({
    queryKey: ['admin-reviews'],
    queryFn: () => api<{ items: any[] }>('/api/admin/reviews'),
  });

  const { data: tokensData, isLoading: tokensLoading } = useQuery({
    queryKey: ['admin-tokens'],
    queryFn: () => api<{ tokens: ApiToken[] }>('/api/admin/tokens'),
  });

  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<string>('all');
  const [sellerStatusFilter, setSellerStatusFilter] = useState<string>('all');
  const [listingStatusFilter, setListingStatusFilter] = useState<string>('all');

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      api(`/api/admin/orders/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      toast.success('Status updated');
    }
  });

  const updateCountryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<CountryConfig> }) =>
      api(`/api/admin/countries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] });
      toast.success('Country updated');
    }
  });

  const addCountryMutation = useMutation({
    mutationFn: (data: Partial<CountryConfig>) =>
      api('/api/admin/countries', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] });
      toast.success('Country added');
    }
  });

  const addIntegrationMutation = useMutation({
    mutationFn: (data: Partial<IntegrationProviderConfig>) =>
      api('/api/admin/integrations', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      toast.success('Integration provider added');
    }
  });

  const updateIntegrationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<IntegrationProviderConfig> }) =>
      api(`/api/admin/integrations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      toast.success('Integration provider updated');
    }
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api(`/api/admin/subscriptions/${id}/verify-payment`, {
        method: 'PATCH'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      toast.success('Payment verified');
    }
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api(`/api/admin/subscriptions/${id}/cancel`, {
        method: 'PATCH'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      toast.success('Subscription cancelled');
    }
  });

  const verifySellerMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: 'verified' | 'rejected' }) =>
      api(`/api/admin/sellers/${id}/verification`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sellers'] });
      toast.success('Seller verification updated');
    }
  });

  const updateSellerStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      api(`/api/admin/sellers/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sellers'] });
      toast.success('Seller status updated');
    }
  });

  const updateListingStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      api(`/api/admin/listings/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-listings'] });
      toast.success('Listing status updated');
    }
  });

  const updateReviewStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      api(`/api/admin/reviews/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
      toast.success('Review status updated');
    }
  });

  const createTokenMutation = useMutation({
    mutationFn: (data: { userId: string, description: string, scopes: TokenScope[], expiresInDays?: number }) =>
      api<{ token: string; id: string }>('/api/admin/tokens', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token created');
    }
  });

  const revokeTokenMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api(`/api/admin/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token revoked');
    }
  });

  const rotateTokenMutation = useMutation({
    mutationFn: ({ id, expiresInDays }: { id: string, expiresInDays?: number }) =>
      api<{ token: string; expiresAt: number }>(`/api/admin/tokens/${id}/rotate`, {
        method: 'POST',
        body: JSON.stringify({ expiresInDays })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token rotated');
    }
  });

  if (statsLoading || ordersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
);
}

function TokensTab() {
  const queryClient = useQueryClient();
  const { data: tokensData, isLoading: tokensLoading } = useQuery({
    queryKey: ['admin-tokens'],
    queryFn: () => api<{ tokens: ApiToken[] }>('/api/admin/tokens'),
  });

  const createTokenMutation = useMutation({
    mutationFn: (data: { userId: string, description: string, scopes: TokenScope[], expiresInDays?: number }) =>
      api<{ token: string; id: string }>('/api/admin/tokens', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token created');
    }
  });

  const revokeTokenMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api(`/api/admin/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token revoked');
    }
  });

  const rotateTokenMutation = useMutation({
    mutationFn: ({ id, expiresInDays }: { id: string, expiresInDays?: number }) =>
      api<{ token: string; expiresAt: number }>(`/api/admin/tokens/${id}/rotate`, {
        method: 'POST',
        body: JSON.stringify({ expiresInDays })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Token rotated');
    }
  });

  if (tokensLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-muted-foreground">Manage API tokens for production access. Tokens expire in 90 days by default.</p>
        <CreateTokenDialog createTokenMutation={createTokenMutation} />
      </div>
      <div className="border rounded-md overflow-hidden bg-background shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
              <tr>
                <th className="px-4 py-4 min-w-[100px]">ID</th>
                <th className="px-4 py-4 min-w-[150px]">User</th>
                <th className="px-4 py-4 min-w-[200px]">Description</th>
                <th className="px-4 py-4 min-w-[120px]">Scopes</th>
                <th className="px-4 py-4 min-w-[100px]">Created</th>
                <th className="px-4 py-4 min-w-[100px]">Expires</th>
                <th className="px-4 py-4 min-w-[100px]">Last Used</th>
                <th className="px-4 py-4 text-right min-w-[150px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(tokensData?.tokens || []).map((token) => (
                <TokenRow
                  key={token.id}
                  token={token}
                  revokeTokenMutation={revokeTokenMutation}
                  rotateTokenMutation={rotateTokenMutation}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CreateTokenDialog({ createTokenMutation }: { createTokenMutation: any }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    description: '',
    scopes: [] as TokenScope[],
    expiresInDays: '90',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createTokenMutation.mutate({
      userId: formData.userId,
      description: formData.description,
      scopes: formData.scopes,
      expiresInDays: formData.expiresInDays ? parseInt(formData.expiresInDays) : undefined,
    });
    setOpen(false);
    setFormData({ userId: '', description: '', scopes: [], expiresInDays: '90' });
  };

  const toggleScope = (scope: TokenScope) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8 text-[10px] rounded-sm">
          <Plus className="h-3 w-3 mr-1" /> Create Token
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-widest">Create API Token</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId" className="text-xs">User ID</Label>
            <Input
              id="userId"
              placeholder="user-123"
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              required
              className="h-9 text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description" className="text-xs">Description</Label>
            <Input
              id="description"
              placeholder="Production API access"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="h-9 text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Scopes</Label>
            <div className="flex gap-4 py-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scope-admin"
                  checked={formData.scopes.includes('admin')}
                  onCheckedChange={() => toggleScope('admin')}
                />
                <label htmlFor="scope-admin" className="text-xs cursor-pointer">Admin</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scope-user"
                  checked={formData.scopes.includes('user')}
                  onCheckedChange={() => toggleScope('user')}
                />
                <label htmlFor="scope-user" className="text-xs cursor-pointer">User</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scope-readonly"
                  checked={formData.scopes.includes('readonly')}
                  onCheckedChange={() => toggleScope('readonly')}
                />
                <label htmlFor="scope-readonly" className="text-xs cursor-pointer">Readonly</label>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiresInDays" className="text-xs">Expires In (days)</Label>
            <Input
              id="expiresInDays"
              type="number"
              placeholder="90"
              value={formData.expiresInDays}
              onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
              className="h-9 text-xs"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-9 text-xs">
              Cancel
            </Button>
            <Button type="submit" className="h-9 text-xs">
              <Plus className="h-3 w-3 mr-1" /> Create Token
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TokenRow({ token, revokeTokenMutation, rotateTokenMutation }: {
  token: ApiToken;
  revokeTokenMutation: any;
  rotateTokenMutation: any;
}) {
  const isExpired = token.expiresAt > 0 && token.expiresAt < Date.now();

  const formatDate = (ts: number) => ts ? new Date(ts).toLocaleDateString() : 'Never';

  return (
    <tr className={cn("hover:bg-muted/5", isExpired && "bg-red-50/50")}>
      <td className="px-4 py-4 font-mono text-[10px]">{token.id?.slice(0, 8) || 'N/A'}</td>
      <td className="px-4 py-4 font-bold">{token.userId}</td>
      <td className="px-4 py-4 text-muted-foreground">{token.description}</td>
      <td className="px-4 py-4">
        <div className="flex gap-1">
          {token.scopes.map(scope => (
            <Badge key={scope} variant="outline" className="text-[9px] px-1 py-0">
              {scope}
            </Badge>
          ))}
        </div>
      </td>
      <td className="px-4 py-4 text-muted-foreground">
        {formatDate(token.createdAt)}
      </td>
      <td className="px-4 py-4">
        <span className={cn(isExpired && "text-red-600 font-bold")}>
          {formatDate(token.expiresAt)}
        </span>
      </td>
      <td className="px-4 py-4 text-muted-foreground">
        {formatDate(token.lastUsedAt)}
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[10px] rounded-sm"
            onClick={() => rotateTokenMutation.mutate({ id: token.id })}
          >
            <RefreshCcw className="h-3 w-3 mr-1" /> Rotate
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[10px] rounded-sm text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => {
              if (confirm('Are you sure you want to revoke this token?')) {
                revokeTokenMutation.mutate({ id: token.id });
              }
            }}
          >
            Revoke
          </Button>
        </div>
      </td>
    </tr>
  );
}

  const chartData = stats?.monthlyRevenue || [];
  const categoryData = stats?.categoryDistribution || [];

  const activeSubscriptions = subscriptionsData?.items.filter(s => s.status === 'active').length || 0;
  const expiredSubscriptions = subscriptionsData?.items.filter(s => s.status === 'expired').length || 0;
  const monthlyRevenue = subscriptionsData?.items
    .filter(s => s.status === 'active')
    .reduce((sum, s) => {
      const plan = subscriptionPlansData?.find(p => p.id === s.planId);
      return sum + (plan?.price || 0);
    }, 0) || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex justify-between items-end border-b pb-6">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Farm Operations Dashboard</h1>
            <p className="text-sm text-muted-foreground">Comprehensive system analytics and order fulfillment.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries()} className="h-8 rounded-md">
            <RefreshCcw className="h-3 w-3 mr-2" /> Refresh System
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatBox label="Total Revenue" value={formatFromBase(stats?.totalRevenue ?? 0)} icon={<DollarSign className="h-4 w-4" />} />
          <StatBox label="Order Volume" value={stats?.orderCount ?? 0} icon={<Package className="h-4 w-4" />} />
          <StatBox label="Growth Rate" value={`${stats?.growthRate ?? 0}%`} icon={<TrendingUp className="h-4 w-4" />} />
          <StatBox
            label="Low Stock Alerts"
            value={stats?.lowStockItems ?? 0}
            icon={<AlertTriangle className="h-4 w-4" />}
            status={stats?.lowStockItems && stats.lowStockItems > 0 ? 'warning' : 'normal'}
          />
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border rounded-md p-6 bg-background shadow-sm">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-6">Revenue Growth</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => formatFromBase(Number(value), { maximumFractionDigits: 0 })}
                  />
                  <Tooltip 
                    formatter={(value: number) => formatFromBase(value)}
                    cursor={{ fill: '#f3f4f6' }} 
                    contentStyle={{ fontSize: '11px', borderRadius: '4px' }}
                  />
                  <Bar dataKey="rev" fill="#111827" radius={[2, 2, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border rounded-md p-6 bg-background shadow-sm">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-6">Sales by Category</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {categoryData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '4px' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-transparent h-auto p-0 gap-8 border-b w-full justify-start rounded-none overflow-x-auto">
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Active Orders</TabsTrigger>
            <TabsTrigger value="inventory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Inventory Health</TabsTrigger>
            <TabsTrigger value="countries" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Countries</TabsTrigger>
            <TabsTrigger value="integrations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Integrations</TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Subscriptions</TabsTrigger>
            <TabsTrigger value="sellers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Sellers</TabsTrigger>
            <TabsTrigger value="listings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Listings</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">Reviews</TabsTrigger>
            <TabsTrigger value="tokens" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2 text-xs font-bold uppercase tracking-widest transition-none">API Tokens</TabsTrigger>
          </TabsList>
          <TabsContent value="orders">
            <div className="border rounded-md overflow-hidden bg-background shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                    <tr>
                      <th className="px-4 py-4 min-w-[100px]">Ref</th>
                      <th className="px-4 py-4 min-w-[150px]">Client</th>
                      <th className="px-4 py-4 min-w-[100px]">Type</th>
                      <th className="px-4 py-4 text-right min-w-[100px]">Settled</th>
                      <th className="px-4 py-4 min-w-[100px]">Status</th>
                      <th className="px-4 py-4 text-right min-w-[150px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {ordersData?.items.map((order) => (
                      <tr key={order.id} className="hover:bg-muted/5">
                        <td className="px-4 py-4 font-mono text-[10px]">{order.id.slice(0, 8)}</td>
                        <td className="px-4 py-4">
                          <div className="font-bold">{order.shippingDetails?.fullname || 'Anonymous'}</div>
                          <div className="text-[10px] text-muted-foreground">{order.shippingDetails?.city}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={cn(
                            "px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-tighter",
                            order.orderType === 'subscription' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                          )}>
                            {order.orderType}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right font-bold">{formatFromBase(order.totalPrice)}</td>
                        <td className="px-4 py-4">
                          <span className={cn(
                            "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
                            order.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                          )}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {order.status === 'pending' && (
                              <Button size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => updateStatusMutation.mutate({ id: order.id, status: 'shipped' })}>Dispatch</Button>
                            )}
                            {order.status === 'shipped' && (
                              <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => updateStatusMutation.mutate({ id: order.id, status: 'delivered' })}>Deliver</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="inventory">
            <InventoryTab />
          </TabsContent>
          <TabsContent value="countries">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">Manage supported countries, currencies, and exchange rates for global expansion.</p>
                <AddCountryDialog addCountryMutation={addCountryMutation} />
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[80px]">Flag</th>
                        <th className="px-4 py-4 min-w-[120px]">Country</th>
                        <th className="px-4 py-4 min-w-[80px]">Code</th>
                        <th className="px-4 py-4 min-w-[100px]">Currency</th>
                        <th className="px-4 py-4 text-right min-w-[120px]">Exchange Rate</th>
                        <th className="px-4 py-4 min-w-[100px]">Status</th>
                        <th className="px-4 py-4 text-right min-w-[150px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {countriesData?.items.map((country) => (
                        <CountryRow 
                          key={country.id} 
                          country={country} 
                          updateCountryMutation={updateCountryMutation}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="integrations">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">Dependency-injected providers for transactions, SMS, email, and WhatsApp by country/locale.</p>
                <AddIntegrationDialog addIntegrationMutation={addIntegrationMutation} />
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[120px]">Name</th>
                        <th className="px-4 py-4 min-w-[100px]">Channel</th>
                        <th className="px-4 py-4 min-w-[120px]">Provider Key</th>
                        <th className="px-4 py-4 min-w-[160px]">Countries</th>
                        <th className="px-4 py-4 min-w-[80px] text-right">Priority</th>
                        <th className="px-4 py-4 min-w-[100px]">Status</th>
                        <th className="px-4 py-4 min-w-[120px] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(integrationsData?.items || []).map((provider) => (
                        <IntegrationRow
                          key={provider.id}
                          provider={provider}
                          updateIntegrationMutation={updateIntegrationMutation}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="subscriptions">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold uppercase tracking-widest">Subscription Management</h3>
                <Select value={subscriptionStatusFilter} onValueChange={setSubscriptionStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-[140px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="pending_payment">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatBox label="Active Subscriptions" value={activeSubscriptions} icon={<Check className="h-4 w-4" />} />
                <StatBox label="Expired Subscriptions" value={expiredSubscriptions} icon={<X className="h-4 w-4" />} />
                <StatBox label="Monthly Revenue" value={formatFromBase(monthlyRevenue)} icon={<DollarSign className="h-4 w-4" />} />
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[100px]">User ID</th>
                        <th className="px-4 py-4 min-w-[150px]">Plan</th>
                        <th className="px-4 py-4 min-w-[100px]">Status</th>
                        <th className="px-4 py-4 min-w-[120px]">Start Date</th>
                        <th className="px-4 py-4 min-w-[120px]">End Date</th>
                        <th className="px-4 py-4 min-w-[100px]">Auto-Renew</th>
                        <th className="px-4 py-4 text-right min-w-[200px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {subscriptionsData?.items
                        .filter((s) => subscriptionStatusFilter === 'all' || s.status === subscriptionStatusFilter)
                        .map((subscription) => (
                          <SubscriptionRow 
                            key={subscription.id} 
                            subscription={subscription}
                            plans={subscriptionPlansData || []}
                            verifyPaymentMutation={verifyPaymentMutation}
                            cancelSubscriptionMutation={cancelSubscriptionMutation}
                          />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="sellers">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold uppercase tracking-widest">Seller Management</h3>
                <Select value={sellerStatusFilter} onValueChange={setSellerStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-[140px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="unverified">Unverified</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[100px]">ID</th>
                        <th className="px-4 py-4 min-w-[150px]">Business Name</th>
                        <th className="px-4 py-4 min-w-[100px]">Location</th>
                        <th className="px-4 py-4 min-w-[100px]">Verification</th>
                        <th className="px-4 py-4 min-w-[80px]">Listings</th>
                        <th className="px-4 py-4 min-w-[80px]">Rating</th>
                        <th className="px-4 py-4 text-right min-w-[200px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sellersData?.items
                        .filter((s) => sellerStatusFilter === 'all' || s.verificationStatus === sellerStatusFilter)
                        .map((seller) => (
                          <SellerRow 
                            key={seller.id} 
                            seller={seller}
                            verifySellerMutation={verifySellerMutation}
                            updateSellerStatusMutation={updateSellerStatusMutation}
                          />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="listings">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold uppercase tracking-widest">Listing Moderation</h3>
                <Select value={listingStatusFilter} onValueChange={setListingStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-[140px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="flagged">Flagged</SelectItem>
                    <SelectItem value="removed">Removed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[100px]">ID</th>
                        <th className="px-4 py-4 min-w-[200px]">Title</th>
                        <th className="px-4 py-4 min-w-[100px]">Type</th>
                        <th className="px-4 py-4 min-w-[100px]">Seller</th>
                        <th className="px-4 py-4 min-w-[100px]">Price</th>
                        <th className="px-4 py-4 min-w-[100px]">Status</th>
                        <th className="px-4 py-4 text-right min-w-[200px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {listingsData?.items
                        .filter((l) => listingStatusFilter === 'all' || l.status === listingStatusFilter)
                        .map((listing) => (
                          <ListingRow 
                            key={listing.id} 
                            listing={listing}
                            updateListingStatusMutation={updateListingStatusMutation}
                          />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="reviews">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold uppercase tracking-widest">Review Moderation</h3>
              </div>
              <div className="border rounded-md overflow-hidden bg-background shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
                      <tr>
                        <th className="px-4 py-4 min-w-[100px]">ID</th>
                        <th className="px-4 py-4 min-w-[150px]">Reviewer</th>
                        <th className="px-4 py-4 min-w-[80px]">Rating</th>
                        <th className="px-4 py-4 min-w-[250px]">Comment</th>
                        <th className="px-4 py-4 min-w-[100px]">Status</th>
                        <th className="px-4 py-4 text-right min-w-[150px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {reviewsData?.items.map((review) => (
                        <ReviewRow 
                          key={review.id} 
                          review={review}
                          updateReviewStatusMutation={updateReviewStatusMutation}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="tokens">
            <TokensTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon, status = 'normal' }: { label: string, value: string | number, icon: React.ReactNode, status?: 'normal' | 'warning' }) {
  return (
    <div className={cn(
      "p-5 border rounded-md shadow-sm space-y-3",
      status === 'warning' ? "bg-orange-50/50 border-orange-200" : "bg-background border-border"
    )}>
      <div className="flex justify-between items-center text-muted-foreground">
        <div className="text-[10px] font-bold uppercase tracking-widest">{label}</div>
        <div className="flex items-center justify-center h-4 w-4">
          {icon}
        </div>
      </div>
      <div className={cn(
        "text-2xl font-bold",
        status === 'warning' ? "text-orange-700" : "text-foreground"
      )}>
        {value}
      </div>
    </div>
  );
}

function CountryRow({ country, updateCountryMutation }: { country: CountryConfig, updateCountryMutation: any }) {
  const [isEditing, setIsEditing] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(country.exchangeRate.toString());
  
  const handleSave = () => {
    updateCountryMutation.mutate({
      id: country.id,
      data: { exchangeRate: parseFloat(exchangeRate) }
    });
    setIsEditing(false);
  };
  
  const toggleEnabled = () => {
    updateCountryMutation.mutate({
      id: country.id,
      data: { enabled: !country.enabled }
    });
  };
  
  return (
    <tr className={cn("hover:bg-muted/5", !country.enabled && "opacity-50")}>
      <td className="px-4 py-4 text-2xl">{country.flag}</td>
      <td className="px-4 py-4 font-bold">{country.name}</td>
      <td className="px-4 py-4 font-mono">{country.code}</td>
      <td className="px-4 py-4 font-bold">{country.currency}</td>
      <td className="px-4 py-4 text-right">
        {isEditing ? (
          <Input 
            type="number" 
            step="0.01"
            value={exchangeRate} 
            onChange={(e) => setExchangeRate(e.target.value)}
            className="h-7 w-24 text-xs text-right"
          />
        ) : (
          <span className="font-mono">{country.exchangeRate}</span>
        )}
      </td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          country.enabled ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
        )}>
          {country.enabled ? 'Active' : 'Disabled'}
        </span>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          {isEditing ? (
            <Button size="sm" className="h-7 text-[10px] rounded-sm" onClick={handleSave}>
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm" onClick={() => setIsEditing(true)}>
              Edit Rate
            </Button>
          )}
          <Button 
            size="sm" 
            variant={country.enabled ? "outline" : "default"}
            className="h-7 text-[10px] rounded-sm" 
            onClick={toggleEnabled}
          >
            {country.enabled ? <ToggleLeft className="h-3 w-3 mr-1" /> : <ToggleRight className="h-3 w-3 mr-1" />}
            {country.enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function AddCountryDialog({ addCountryMutation }: { addCountryMutation: any }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    flag: '',
    currency: '',
    exchangeRate: '1',
    locale: '',
    timezone: ''
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addCountryMutation.mutate({
      ...formData,
      exchangeRate: parseFloat(formData.exchangeRate)
    });
    setOpen(false);
    setFormData({
      code: '',
      name: '',
      flag: '',
      currency: '',
      exchangeRate: '1',
      locale: '',
      timezone: ''
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8 text-[10px] rounded-sm">
          <Plus className="h-3 w-3 mr-1" /> Add Country
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-widest">Add New Country</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs">Country Code</Label>
              <Input 
                id="code" 
                placeholder="GH" 
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                maxLength={2}
                required
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flag" className="text-xs">Flag Emoji</Label>
              <Input 
                id="flag" 
                placeholder="🇬🇭" 
                value={formData.flag}
                onChange={(e) => setFormData({...formData, flag: e.target.value})}
                maxLength={4}
                required
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs">Country Name</Label>
            <Input 
              id="name" 
              placeholder="Ghana" 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="h-9 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency" className="text-xs">Currency Code</Label>
              <Input 
                id="currency" 
                placeholder="GHS" 
                value={formData.currency}
                onChange={(e) => setFormData({...formData, currency: e.target.value.toUpperCase()})}
                maxLength={3}
                required
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="exchangeRate" className="text-xs">Exchange Rate (vs USD)</Label>
              <Input 
                id="exchangeRate" 
                type="number" 
                step="0.01"
                placeholder="15.6" 
                value={formData.exchangeRate}
                onChange={(e) => setFormData({...formData, exchangeRate: e.target.value})}
                required
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="locale" className="text-xs">Locale</Label>
              <Input 
                id="locale" 
                placeholder="en-GH" 
                value={formData.locale}
                onChange={(e) => setFormData({...formData, locale: e.target.value})}
                required
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone" className="text-xs">Timezone</Label>
              <Input 
                id="timezone" 
                placeholder="Africa/Accra" 
                value={formData.timezone}
                onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                required
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-9 text-xs">
              Cancel
            </Button>
            <Button type="submit" className="h-9 text-xs">
              <Plus className="h-3 w-3 mr-1" /> Add Country
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IntegrationRow({ provider, updateIntegrationMutation }: { provider: IntegrationProviderConfig, updateIntegrationMutation: any }) {
  const [priority, setPriority] = useState(provider.priority.toString());

  const toggleEnabled = () => {
    updateIntegrationMutation.mutate({
      id: provider.id,
      data: { enabled: !provider.enabled }
    });
  };

  const savePriority = () => {
    const parsed = Number(priority);
    if (Number.isNaN(parsed)) return;
    updateIntegrationMutation.mutate({
      id: provider.id,
      data: { priority: parsed }
    });
  };

  return (
    <tr className={cn("hover:bg-muted/5", !provider.enabled && "opacity-50")}>
      <td className="px-4 py-4 font-bold">{provider.name}</td>
      <td className="px-4 py-4 uppercase">{provider.channel}</td>
      <td className="px-4 py-4 font-mono text-[10px]">{provider.providerKey}</td>
      <td className="px-4 py-4">{provider.countryCodes.length ? provider.countryCodes.join(', ') : 'All'}</td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          <Input
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="h-7 w-16 text-right text-xs"
            type="number"
          />
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={savePriority}>
            <Save className="h-3 w-3 mr-1" /> Save
          </Button>
        </div>
      </td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          provider.enabled ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
        )}>
          {provider.enabled ? 'Active' : 'Disabled'}
        </span>
      </td>
      <td className="px-4 py-4 text-right flex gap-2 justify-end">
        <Button
          size="sm"
          variant={provider.enabled ? 'outline' : 'default'}
          className="h-7 text-[10px]"
          onClick={toggleEnabled}
        >
          {provider.enabled ? <ToggleLeft className="h-3 w-3 mr-1" /> : <ToggleRight className="h-3 w-3 mr-1" />}
          {provider.enabled ? 'Disable' : 'Enable'}
        </Button>
      </td>
    </tr>
  );
}

function AddIntegrationDialog({ addIntegrationMutation }: { addIntegrationMutation: any }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    providerKey: '',
    channel: 'transactions' as IntegrationChannel,
    countryCodes: '',
    locales: '',
    priority: '100',
    isDefault: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addIntegrationMutation.mutate({
      name: formData.name,
      providerKey: formData.providerKey,
      channel: formData.channel,
      countryCodes: formData.countryCodes.split(',').map((item) => item.trim().toUpperCase()).filter(Boolean),
      locales: formData.locales.split(',').map((item) => item.trim()).filter(Boolean),
      priority: Number(formData.priority),
      isDefault: formData.isDefault,
      enabled: true,
      settings: {},
    });
    setOpen(false);
    setFormData({
      name: '',
      providerKey: '',
      channel: 'transactions',
      countryCodes: '',
      locales: '',
      priority: '100',
      isDefault: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8 text-[10px] rounded-sm">
          <Plus className="h-3 w-3 mr-1" /> Add Provider
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-widest">Add Integration Provider</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Provider Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-9 text-xs" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Provider Key</Label>
              <Input value={formData.providerKey} onChange={(e) => setFormData({ ...formData, providerKey: e.target.value })} placeholder="mock_twilio" required className="h-9 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Channel</Label>
              <Select value={formData.channel} onValueChange={(value) => setFormData({ ...formData, channel: value as IntegrationChannel })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transactions">transactions</SelectItem>
                  <SelectItem value="sms">sms</SelectItem>
                  <SelectItem value="email">email</SelectItem>
                  <SelectItem value="whatsapp">whatsapp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Priority</Label>
              <Input type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="h-9 text-xs" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Country Codes (comma-separated)</Label>
            <Input value={formData.countryCodes} onChange={(e) => setFormData({ ...formData, countryCodes: e.target.value })} placeholder="GH, NG" className="h-9 text-xs" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Locales (comma-separated)</Label>
            <Input value={formData.locales} onChange={(e) => setFormData({ ...formData, locales: e.target.value })} placeholder="en-GH, en-NG" className="h-9 text-xs" />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="default-provider"
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            />
            <Label htmlFor="default-provider" className="text-xs">Set as default fallback</Label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" className="h-9 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" className="h-9 text-xs"><Plus className="h-3 w-3 mr-1" /> Add Provider</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionRow({ subscription, plans, verifyPaymentMutation, cancelSubscriptionMutation }: { 
  subscription: any, 
  plans: any[], 
  verifyPaymentMutation: any, 
  cancelSubscriptionMutation: any 
}) {
  const plan = plans.find(p => p.id === subscription.planId);
  
  return (
    <tr className="hover:bg-muted/5">
      <td className="px-4 py-4 font-mono text-[10px]">{subscription.userId?.slice(0, 8) || 'N/A'}</td>
      <td className="px-4 py-4 font-bold">{plan?.name || subscription.planId}</td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          subscription.status === 'active' && 'bg-green-100 text-green-700',
          subscription.status === 'expired' && 'bg-red-100 text-red-700',
          subscription.status === 'cancelled' && 'bg-gray-100 text-gray-700',
          subscription.status === 'pending_payment' && 'bg-yellow-100 text-yellow-700'
        )}>
          {subscription.status}
        </span>
      </td>
      <td className="px-4 py-4 text-muted-foreground">
        {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'N/A'}
      </td>
      <td className="px-4 py-4 text-muted-foreground">
        {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
      </td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          subscription.autoRenew ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground'
        )}>
          {subscription.autoRenew ? 'Yes' : 'No'}
        </span>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          {subscription.status === 'pending_payment' && (
            <Button 
              size="sm" 
              className="h-7 text-[10px] rounded-sm" 
              onClick={() => verifyPaymentMutation.mutate({ id: subscription.id })}
            >
              <Check className="h-3 w-3 mr-1" /> Verify Payment
            </Button>
          )}
          {subscription.status === 'active' && (
            <Button 
              size="sm" 
              variant="outline" 
              className="h-7 text-[10px] rounded-sm" 
              onClick={() => cancelSubscriptionMutation.mutate({ id: subscription.id })}
            >
              <X className="h-3 w-3 mr-1" /> Cancel
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function SellerRow({ seller, verifySellerMutation, updateSellerStatusMutation }: { 
  seller: any, 
  verifySellerMutation: any, 
  updateSellerStatusMutation: any 
}) {
  return (
    <tr className="hover:bg-muted/5">
      <td className="px-4 py-4 font-mono text-[10px]">{seller.id?.slice(0, 8) || 'N/A'}</td>
      <td className="px-4 py-4 font-bold">{seller.businessName}</td>
      <td className="px-4 py-4 text-muted-foreground">{seller.location}</td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          seller.verificationStatus === 'verified' && 'bg-green-100 text-green-700',
          seller.verificationStatus === 'pending' && 'bg-yellow-100 text-yellow-700',
          seller.verificationStatus === 'unverified' && 'bg-gray-100 text-gray-700'
        )}>
          {seller.verificationStatus}
        </span>
      </td>
      <td className="px-4 py-4 text-right font-bold">{seller.activeListings || 0}</td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-1">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-bold">{seller.rating?.toFixed(1) || 'N/A'}</span>
        </div>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          {seller.verificationStatus === 'pending' && (
            <>
              <Button 
                size="sm" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => verifySellerMutation.mutate({ id: seller.id, status: 'verified' })}
              >
                <Check className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => verifySellerMutation.mutate({ id: seller.id, status: 'rejected' })}
              >
                <X className="h-3 w-3 mr-1" /> Reject
              </Button>
            </>
          )}
          {seller.verificationStatus === 'verified' && (
            <Button 
              size="sm" 
              variant="outline" 
              className="h-7 text-[10px] rounded-sm" 
              onClick={() => updateSellerStatusMutation.mutate({ id: seller.id, status: 'suspended' })}
            >
              <X className="h-3 w-3 mr-1" /> Suspend
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ListingRow({ listing, updateListingStatusMutation }: { 
  listing: any, 
  updateListingStatusMutation: any 
}) {
  return (
    <tr className="hover:bg-muted/5">
      <td className="px-4 py-4 font-mono text-[10px]">{listing.id?.slice(0, 8) || 'N/A'}</td>
      <td className="px-4 py-4 font-bold">{listing.title}</td>
      <td className="px-4 py-4 capitalize">{listing.farmingDomain || listing.category}</td>
      <td className="px-4 py-4 text-muted-foreground">{listing.sellerId?.slice(0, 8)}</td>
      <td className="px-4 py-4 font-bold">
        {listing.currency || '₵'}{listing.price?.toLocaleString() || listing.pricePerUnit?.toLocaleString()}
      </td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          listing.status === 'active' && 'bg-green-100 text-green-700',
          listing.status === 'pending_review' && 'bg-yellow-100 text-yellow-700',
          listing.status === 'flagged' && 'bg-red-100 text-red-700',
          listing.status === 'removed' && 'bg-gray-100 text-gray-700'
        )}>
          {listing.status}
        </span>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          {listing.status === 'pending_review' && (
            <>
              <Button 
                size="sm" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => updateListingStatusMutation.mutate({ id: listing.id, status: 'active' })}
              >
                <Check className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => updateListingStatusMutation.mutate({ id: listing.id, status: 'flagged' })}
              >
                <X className="h-3 w-3 mr-1" /> Flag
              </Button>
            </>
          )}
          {listing.status === 'active' && (
            <Button 
              size="sm" 
              variant="outline" 
              className="h-7 text-[10px] rounded-sm" 
              onClick={() => updateListingStatusMutation.mutate({ id: listing.id, status: 'removed' })}
            >
              <X className="h-3 w-3 mr-1" /> Remove
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ReviewRow({ review, updateReviewStatusMutation }: { 
  review: any, 
  updateReviewStatusMutation: any 
}) {
  return (
    <tr className="hover:bg-muted/5">
      <td className="px-4 py-4 font-mono text-[10px]">{review.id?.slice(0, 8) || 'N/A'}</td>
      <td className="px-4 py-4 font-bold">{review.reviewerName}</td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`h-3 w-3 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
            />
          ))}
        </div>
      </td>
      <td className="px-4 py-4 text-muted-foreground max-w-[250px] truncate">{review.comment}</td>
      <td className="px-4 py-4">
        <span className={cn(
          "px-2 py-0.5 rounded-sm font-bold text-[9px] uppercase",
          review.status === 'active' && 'bg-green-100 text-green-700',
          review.status === 'flagged' && 'bg-red-100 text-red-700',
          review.status === 'removed' && 'bg-gray-100 text-gray-700'
        )}>
          {review.status}
        </span>
      </td>
      <td className="px-4 py-4 text-right">
        <div className="flex justify-end gap-2">
          {review.status === 'flagged' && (
            <>
              <Button 
                size="sm" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => updateReviewStatusMutation.mutate({ id: review.id, status: 'active' })}
              >
                <Check className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 text-[10px] rounded-sm" 
                onClick={() => updateReviewStatusMutation.mutate({ id: review.id, status: 'removed' })}
              >
                <X className="h-3 w-3 mr-1" /> Remove
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function InventoryTab() {
  const queryClient = useQueryClient();
  const { formatFromBase } = useCurrency();
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [threshold, setThreshold] = useState('10');

  const { data: inventoryData } = useQuery({
    queryKey: ['admin-inventory'],
    queryFn: () => api<{ items: any[] }>('/api/admin/inventory'),
  });

  const restockMutation = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) =>
      api(`/api/admin/inventory/${id}/restock`, {
        method: 'PATCH',
        body: JSON.stringify({ quantity }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-inventory'] });
      toast.success('Product restocked');
    },
  });

  const products = inventoryData?.items || [];
  const allProducts = products;
  const lowStockProducts = products.filter((p: any) => p.isLowStock && !p.isOutOfStock);
  const outOfStockProducts = products.filter((p: any) => p.isOutOfStock);

  const filteredProducts = filter === 'all' ? allProducts : filter === 'low' ? lowStockProducts : outOfStockProducts;
  const lowThreshold = parseInt(threshold, 10);

  const getBadge = (product: any) => {
    const stock = product.stockCount || 0;
    const prodThreshold = product.lowStockThreshold || lowThreshold;
    if (stock === 0) {
      return <Badge variant="destructive" className="text-[9px]">Out of Stock</Badge>;
    }
    if (stock < prodThreshold) {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-[9px]">Low Stock</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 text-[9px]">In Stock</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <Select value={filter} onValueChange={(v: 'all' | 'low' | 'out') => setFilter(v)}>
          <SelectTrigger className="h-8 text-xs w-[140px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="low">Low Stock</SelectItem>
            <SelectItem value="out">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          className="h-8 w-24 text-xs"
          placeholder="Threshold"
        />
        <div className="flex gap-2 text-xs">
          <span className="flex items-center gap-1">
            <Badge variant="secondary" className="bg-orange-100 text-orange-700 h-4">Low: {lowStockProducts.length}</Badge>
          </span>
          <span className="flex items-center gap-1">
            <Badge variant="destructive" className="h-4">Out: {outOfStockProducts.length}</Badge>
          </span>
        </div>
      </div>

      {lowStockProducts.length > 0 && (
        <div className="border border-orange-200 rounded-md bg-orange-50/50 p-4">
          <h4 className="text-xs font-bold uppercase tracking-widest text-orange-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Low Stock Alerts ({lowStockProducts.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStockProducts.slice(0, 6).map((product: any) => (
              <div key={product.id} className="flex items-center justify-between bg-background border rounded p-3">
                <div>
                  <div className="font-bold text-xs">{product.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {product.stockCount} {product.unit} remaining (threshold: {product.lowStockThreshold})
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  onClick={() => restockMutation.mutate({ id: product.id, quantity: 50 })}
                >
                  +50 Restock
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {outOfStockProducts.length > 0 && (
        <div className="border border-red-200 rounded-md bg-red-50/50 p-4">
          <h4 className="text-xs font-bold uppercase tracking-widest text-red-800 mb-3 flex items-center gap-2">
            <Package className="h-4 w-4" /> Out of Stock ({outOfStockProducts.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {outOfStockProducts.slice(0, 6).map((product: any) => (
              <div key={product.id} className="flex items-center justify-between bg-background border border-red-200 rounded p-3">
                <div>
                  <div className="font-bold text-xs">{product.name}</div>
                  <div className="text-[10px] text-red-600">Needs immediate restock</div>
                </div>
                <Button
                  size="sm"
                  className="h-7 text-[10px]"
                  onClick={() => restockMutation.mutate({ id: product.id, quantity: 100 })}
                >
                  +100 Restock
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border rounded-md overflow-hidden bg-background shadow-sm overflow-x-auto">
        <table className="w-full text-xs text-left">
          <thead className="bg-muted/50 text-muted-foreground border-b font-bold uppercase tracking-widest">
            <tr>
              <th className="px-4 py-4 min-w-[150px]">Product</th>
              <th className="px-4 py-4 min-w-[120px]">Category</th>
              <th className="px-4 py-4 min-w-[100px]">Stock Level</th>
              <th className="px-4 py-4 min-w-[80px]">Threshold</th>
              <th className="px-4 py-4 text-right min-w-[80px]">Price</th>
              <th className="px-4 py-4 min-w-[100px]">Status</th>
              <th className="px-4 py-4 min-w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredProducts.map((product: any) => (
              <tr key={product.id} className={cn("hover:bg-muted/5", product.isOutOfStock && "bg-red-50/50")}>
                <td className="px-4 py-4 font-bold">{product.name}</td>
                <td className="px-4 py-4 capitalize text-muted-foreground">{product.category} - {product.subcategory}</td>
                <td className="px-4 py-4">
                  <span className={cn(
                    "font-bold",
                    product.isOutOfStock ? "text-red-600" : product.isLowStock ? "text-orange-600" : "text-foreground"
                  )}>
                    {product.stockCount} {product.unit}
                  </span>
                </td>
                <td className="px-4 py-4 text-muted-foreground">{product.lowStockThreshold}</td>
                <td className="px-4 py-4 text-right font-bold">{formatFromBase(product.price)}</td>
                <td className="px-4 py-4">{getBadge(product)}</td>
                <td className="px-4 py-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px]"
                    onClick={() => restockMutation.mutate({ id: product.id, quantity: 50 })}
                  >
                    +50
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
